/**
 * The transcript. The centrepiece of the product, and the reason the word
 * timings exist.
 *
 * Three things make it more than a wall of text, and all three come from
 * `turns[].words[]`:
 *
 *   1. **The current word is lit as the audio plays.** Not the current line —
 *      the current WORD. That is what makes listening and reading feel like one
 *      activity rather than two.
 *   2. **Clicking any word seeks exactly there.** `t0_ns` is shared between the
 *      transcript and the playback audio, so "exactly" is literal, not
 *      approximate.
 *   3. **Low-confidence words are marked.** An ASR transcript is not evidence;
 *      a word the engine was 60% sure of should not look identical to one it
 *      was certain of. The engine already publishes the number.
 *
 * PERFORMANCE. The clock ticks at animation rate, and a 90-minute session is
 * thousands of words. Re-rendering the document sixty times a second is not an
 * option, so: the active word is computed by binary search inside a subscriber
 * (`useTimeDerived`), only its identity is published, and each turn is a
 * `memo`'d component that receives a word index only when the playhead is
 * inside it. A tick therefore re-renders at most two turn blocks — the one
 * being left and the one being entered.
 */

import * as React from 'react'
import { ArrowDownToLine, Copy, Crosshair, Search, X } from 'lucide-react'
import type { TranscriptDocument, TranscriptTurn } from '@era/shared'
import { Monogram } from './monogram'
import { SpeakerChips } from './speaker-chips'
import { Input } from '@/components/ui/input'
import { formatDuration } from '@/lib/format'
import { cn } from '@/lib/utils'
import { findScroller, spineHeadroom, useCoarsePointer } from '../layout'
import type { SpeakerRoster } from '../speakers'
import { useTimeDerived, type Player } from '../use-player'

/** Below this the engine is guessing, and the reader should be told. */
const LOW_CONFIDENCE = 0.75
/** A silence longer than this gets a marker, so a gap reads as a gap. */
const GAP_MS = 4_000

export function TranscriptPanel({
  document: transcript,
  roster,
  player,
  focusIdentity,
  onFocusIdentity,
}: {
  document: TranscriptDocument
  roster: SpeakerRoster
  player: Player
  /** Set from the speaker rail: show only this person's turns. */
  focusIdentity: string | null
  onFocusIdentity: (identity: string | null) => void
}) {
  const [query, setQuery] = React.useState('')
  const [follow, setFollow] = React.useState(true)

  const turns = transcript.turns

  /* -- the lookup index ---------------------------------------------------- */
  //
  // One flat array of every word in document order, with the turn it belongs
  // to. Built once per transcript; searched per tick.
  const index = React.useMemo(() => buildWordIndex(turns), [turns])

  /**
   * Which word is lit, encoded as ONE number so `Object.is` can decide whether
   * anything changed. `turn * 4096 + word` rather than an object, because an
   * object would be a new identity on every tick and defeat the comparison
   * that makes this affordable.
   */
  const activeKey = useTimeDerived(
    player,
    React.useCallback((timeMs: number) => lookupWord(index, timeMs), [index]),
  )
  const activeTurn = activeKey < 0 ? -1 : activeKey >> 12
  const activeWord = activeKey < 0 ? -1 : activeKey & 0xfff

  /* -- follow the playhead ------------------------------------------------- */
  //
  // Found by attribute rather than by a ref per turn. A `ref` callback written
  // inline is a new function on every render, which would make every turn's
  // props change and defeat the `memo` that the whole design rests on.
  const listRef = React.useRef<HTMLDivElement | null>(null)

  /**
   * Bring a turn into the readable band, if it is not already in it.
   *
   * Three things here were wrong once the app stopped scrolling the document,
   * and all three were invisible on a desktop:
   *
   *   - `scrollIntoView` scrolls EVERY scrollable ancestor, including the
   *     horizontal speaker-chip strip above. Scrolling one named element does
   *     what is meant.
   *   - the old bounds were `140` and `window.innerHeight - 80` — two magic
   *     numbers measured against a window that, now that `Shell` owns the
   *     scrolling, never moves and is not the visible band. The band is the
   *     scroller's box minus whatever pinned chrome is sitting on top of it,
   *     which is a different number on a phone (player + tabs), on a tablet
   *     (nothing) and on a desktop (player only), and different again per
   *     roster size because the ribbon grows a lane per speaker.
   *   - `block: 'center'` centres the turn, which on a phone means the line you
   *     are listening to sits under your own thumb. A third of the way down
   *     leaves the sentences you have not heard yet on screen, which is the
   *     direction reading actually goes.
   */
  const revealTurn = React.useCallback((turnIndex: number, force: boolean) => {
    const list = listRef.current
    const node = list?.querySelector<HTMLElement>(`[data-turn="${turnIndex}"]`)
    const scroller = findScroller(list)
    if (!node || !scroller) return
    const port = scroller.getBoundingClientRect()
    const top = port.top + spineHeadroom(scroller)
    const box = node.getBoundingClientRect()
    // Only scroll if the turn is actually out of comfortable view. Otherwise a
    // smooth-scroll fires on every turn change and the page never settles.
    if (!force && box.top >= top + 8 && box.bottom <= port.bottom - 8) return
    scroller.scrollBy({
      top: box.top - (top + (port.bottom - top) * 0.3),
      // `styles.css` turns transitions off under `prefers-reduced-motion`, but a
      // media query cannot reach a scroll issued from JavaScript — this is the
      // one animation in the app that has to opt out in code. It is also the
      // one most worth opting out of: an unrequested smooth scroll of the whole
      // reading surface is exactly the motion that setting is for.
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }, [])

  React.useEffect(() => {
    // Following a PAUSED player is following nothing: the playhead is not
    // moving, so any scroll it causes is the page moving under a reader who is
    // reading, which is the single most irritating thing this screen could do.
    if (!follow || !player.playing || activeTurn < 0) return
    revealTurn(activeTurn, false)
  }, [activeTurn, follow, player.playing, revealTurn])

  /* -- filtering ----------------------------------------------------------- */
  //
  // Above the scroll listeners rather than below them, because whether the list
  // element exists at all is a filtering outcome and the listener effect has to
  // depend on it.

  const needle = query.trim().toLowerCase()
  const visible = React.useMemo(
    () =>
      turns
        .map((turn, position) => ({ turn, position }))
        .filter(({ turn }) => {
          if (focusIdentity && turn.identity !== focusIdentity) return false
          if (needle && !turn.text.toLowerCase().includes(needle)) return false
          return true
        }),
    [turns, focusIdentity, needle],
  )

  const matchCount = needle
    ? turns.reduce((total, turn) => total + countOccurrences(turn.text.toLowerCase(), needle), 0)
    : 0

  const hasVisibleTurns = visible.length > 0

  /**
   * The user scrolling wins, immediately and permanently, until they ask for it
   * back.
   *
   * Discriminated on the INPUT event, not on the `scroll` event: `scroll` is
   * fired identically by the user's finger and by the smooth scroll two effects
   * up, so a listener on it cannot tell whose scroll it is and ends up either
   * switching follow off every time it follows, or needing a flag and a timeout
   * to paper over its own animation. `wheel` and `touchmove` are only ever
   * produced by a human.
   *
   * The 8px threshold is not decoration. A finger resting on a word to tap it
   * moves two or three pixels, and iOS delivers that as `touchmove` — without
   * the threshold, tapping any word in the transcript silently turns following
   * off, which reads as the feature being broken rather than as a gesture.
   */
  React.useEffect(() => {
    const list = listRef.current
    if (!list) return
    let startY = 0
    const onTouchStart = (event: TouchEvent) => {
      startY = event.touches[0]?.clientY ?? 0
    }
    const onTouchMove = (event: TouchEvent) => {
      if (Math.abs((event.touches[0]?.clientY ?? 0) - startY) > 8) setFollow(false)
    }
    const onWheel = () => setFollow(false)
    list.addEventListener('wheel', onWheel, { passive: true })
    list.addEventListener('touchstart', onTouchStart, { passive: true })
    list.addEventListener('touchmove', onTouchMove, { passive: true })
    return () => {
      list.removeEventListener('wheel', onWheel)
      list.removeEventListener('touchstart', onTouchStart)
      list.removeEventListener('touchmove', onTouchMove)
    }
    // `hasVisibleTurns`, not `[]`. Searching down to nothing unmounts the list
    // and mounts a NEW element when the search is cleared; with an empty
    // dependency array the listeners stay bound to the discarded one and the
    // user's scroll silently stops taking following off — a bug you only meet
    // after typing in the search box, which is why it is spelled out here.
  }, [hasVisibleTurns])

  const resumeFollowing = React.useCallback(() => {
    setFollow(true)
    if (activeTurn >= 0) revealTurn(activeTurn, true)
  }, [activeTurn, revealTurn])

  // Stable identities. Passing an inline arrow to a `memo`'d child is the
  // classic way to make the memo do nothing at all.
  const seekAndPlay = React.useCallback(
    (ms: number) => player.seekMs(ms, { play: true }),
    [player],
  )

  /**
   * Tapping a WORD, on a touch screen, moves the playhead and nothing else.
   *
   * A word is a ~30x26px target inside a wall of other words, so a mis-tap is
   * not a possibility to design against — it is the normal case. With a cursor
   * the cost of one is nothing: you land two words early and click again. On a
   * phone the same mis-tap, with the recording paused, starts a private
   * conversation playing out loud in whatever room the reader is in, and the
   * only way back is to find the pause button. That is not a symmetric outcome,
   * so the touch version does not carry it: the playhead moves, the lit word
   * moves with it so the tap is visibly acknowledged, and if audio was already
   * playing it simply carries on from the new position.
   *
   * Keyed on the POINTER rather than on a width, because the failure is about
   * fingers and an iPhone in landscape is 844px wide. `useCoarsePointer` is the
   * same question `styles.css` asks for its 16px input floor.
   *
   * The turn's timestamp button above each block still plays: it is a labelled,
   * deliberate control that nobody hits by accident, so it keeps the documented
   * "click to play from here" behaviour on every device.
   */
  const coarse = useCoarsePointer()
  const seekFromWord = React.useCallback(
    (ms: number) => player.seekMs(ms, { play: coarse ? player.playing : true }),
    [player, coarse],
  )

  const copyAll = () => {
    const text = turns
      .map((turn) => `[${formatDuration(turn.start_ms)}] ${turn.display_name}: ${turn.text}`)
      .join('\n\n')
    void navigator.clipboard?.writeText(text)
  }

  return (
    <div className="space-y-3 sm:space-y-5">
      {/* -- toolbar --------------------------------------------------------- */}
      {/* Below `sm` the labels come off Follow and Copy so the three controls
          hold one line at 375px instead of wrapping to two. Icon-only is a
          trade — it costs discoverability — and it is paid for by the "jump to
          now" pill below, which is what actually teaches following, and which
          only ever appears at the moment the idea is relevant. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[13rem] flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-ink-dim" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search this transcript"
            className="h-11 pl-9 sm:h-9"
            aria-label="Search this transcript"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute top-1/2 right-1 -translate-y-1/2 rounded p-3 text-ink-dim hover:text-ink sm:right-2 sm:p-1"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </div>

        <ToolbarToggle
          active={follow}
          onClick={() => setFollow((value) => !value)}
          label="Follow the playhead"
        >
          <Crosshair className="size-3.5" />
          <span className="hidden sm:inline">Follow</span>
        </ToolbarToggle>

        <ToolbarToggle active={false} onClick={copyAll} label="Copy the whole transcript">
          <Copy className="size-3.5" />
          <span className="hidden sm:inline">Copy</span>
        </ToolbarToggle>
      </div>

      {/* The speaker filter, phone only.
          At `sm` and up this is the job of `SpeakerRail` down the left, which
          also carries the legend and the talk-share summary. On a phone the
          rail is not rendered at all — see the comment on the grid in
          `session-detail.tsx` — and this row is the part of it that had to
          survive, because it is the part you use while reading. */}
      <SpeakerChips
        roster={roster}
        focusIdentity={focusIdentity}
        onFocus={onFocusIdentity}
        className="sm:hidden"
      />

      {(needle || focusIdentity) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          {needle ? (
            <span>
              <strong className="font-semibold text-ink">{matchCount}</strong>{' '}
              {matchCount === 1 ? 'match' : 'matches'} in {visible.length} of {turns.length} turns
            </span>
          ) : null}
          {focusIdentity ? (
            <button
              type="button"
              onClick={() => onFocusIdentity(null)}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2.5 py-0.5 hover:text-ink"
            >
              only {roster.get(focusIdentity).displayName}
              <X className="size-3" />
            </button>
          ) : null}
        </div>
      )}

      {/* -- the document ---------------------------------------------------- */}
      {/* TWO different emptinesses, and collapsing them told people their
          search was at fault when it was not.
          
          A call in which nobody speaks — or whose microphone captured no
          speech — finishes the pipeline with a VERIFIED transcript whose
          `turns` is empty. The history row shows the green "transcript" pill,
          so someone opens the session expecting words and is told "Nothing in
          this transcript matches", which describes a filter they never set and
          sends them looking for the search box to clear.
          
          The document being empty is a fact about the call. A filter matching
          nothing is a fact about the filter. */}
      {turns.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line bg-surface-2/40 px-5 py-8 text-center text-sm text-ink-muted">
          This call was transcribed and no speech was found in it.
        </p>
      ) : visible.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line bg-surface-2/40 px-5 py-8 text-center text-sm text-ink-muted">
          Nothing in this transcript matches.
        </p>
      ) : (
        <div ref={listRef} className="space-y-1">
          {visible.map(({ turn, position }, listIndex) => {
            const previous = listIndex > 0 ? visible[listIndex - 1]!.turn : null
            const gap = previous ? turn.start_ms - previous.end_ms : 0
            const speaker = roster.get(turn.identity)
            return (
              <React.Fragment key={`${turn.identity}-${turn.start_ms}-${position}`}>
                {gap >= GAP_MS ? <SilenceMarker ms={gap} /> : null}
                <TurnBlock
                  position={position}
                  turn={turn}
                  colorIndex={speaker.index}
                  color={speaker.color}
                  activeWord={activeTurn === position ? activeWord : -1}
                  isActive={activeTurn === position}
                  needle={needle}
                  onSeek={seekAndPlay}
                  onSeekWord={seekFromWord}
                  onFocusSpeaker={onFocusIdentity}
                />
              </React.Fragment>
            )
          })}

          {/* -- back to the playhead ---------------------------------------- */}
          {/*
            The other half of "the user's scroll wins". Taking following away
            the moment somebody scrolls is only kind if there is a way back, and
            a toggle in a toolbar that has scrolled off the top of a
            forty-minute transcript is not one.

            `position: sticky` with a BOTTOM offset, inside the list rather than
            fixed over the app. That is what makes it correct on a phone for
            free: the app's scroller ends above the tab bar, which itself ends
            above the home indicator, so a pill pinned to the bottom of the
            scroller is already clear of both — no `env(safe-area-inset-bottom)`
            here, no z-index race with the tab bar, and nothing to re-pin when
            iOS resizes the visual viewport. It exists only while the audio is
            playing and following is off, which is the only moment it means
            anything.
          */}
          {player.playing && !follow ? (
            <div className="pointer-events-none sticky bottom-3 z-20 flex justify-center pt-2">
              <button
                type="button"
                onClick={resumeFollowing}
                className={cn(
                  'pointer-events-auto inline-flex h-11 items-center gap-2 rounded-full px-5',
                  'bg-ink text-[0.8125rem] font-medium text-paper shadow-[var(--shadow-pop)]',
                  'transition-transform active:scale-95',
                )}
              >
                <ArrowDownToLine className="size-4" />
                Jump to what is playing
              </button>
            </div>
          ) : null}
        </div>
      )}

      <p className="pt-2 text-xs leading-relaxed text-ink-dim">
        {transcript.engine ? (
          <>
            Transcribed by <span className="ident">{transcript.engine}</span>
            {transcript.engine_revision ? ` (${transcript.engine_revision})` : ''}.{' '}
          </>
        ) : null}
        {/* Says what this device actually does, rather than the desktop
            sentence with the verb swapped — see `seekFromWord`. */}
        {coarse ? 'Tap any word to move the playhead to it.' : 'Click any word to play from it.'} A
        dotted underline marks a word the engine was less than{' '}
        {Math.round(LOW_CONFIDENCE * 100)}% sure of.
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* one turn                                                                    */
/* -------------------------------------------------------------------------- */

interface TurnBlockProps {
  /** Position in the document. Rendered as `data-turn`, which is how "follow" finds it. */
  position: number
  turn: TranscriptTurn
  colorIndex: number
  color: string
  /** Index of the lit word, or -1 when the playhead is elsewhere. */
  activeWord: number
  isActive: boolean
  needle: string
  /** The turn's timestamp button: seeks AND plays, on every device. */
  onSeek: (ms: number) => void
  /** A word: seeks, and only plays with a cursor. See `seekFromWord`. */
  onSeekWord: (ms: number) => void
  onFocusSpeaker: (identity: string) => void
}

/**
 * `memo` is load-bearing, not decoration.
 *
 * Every prop here is either a primitive or stable, so a tick that does not
 * change THIS turn's `activeWord` does not re-render it. That is what keeps a
 * thousand-turn transcript at sixty frames a second.
 */
const TurnBlock = React.memo(function TurnBlock({
  position,
  turn,
  colorIndex,
  color,
  activeWord,
  isActive,
  needle,
  onSeek,
  onSeekWord,
  onFocusSpeaker,
}: TurnBlockProps) {
  const words = turn.words.length > 0 ? turn.words : null

  return (
      <div
        data-turn={position}
        className={cn(
          'group relative flex gap-2.5 rounded-lg px-2 py-2.5 transition-colors sm:gap-4 sm:px-3 sm:py-3',
          isActive ? 'bg-surface-2/70' : 'hover:bg-surface-2/40',
        )}
      >
        {/* The speaker's colour rides a rule and the avatar — never the text.
            A light categorical hue is illegible as small type, and identity
            reads better from a mark beside the name than from the name. */}
        <span
          aria-hidden
          className="absolute inset-y-2 left-0 w-[3px] rounded-full transition-opacity"
          style={{ backgroundColor: color, opacity: isActive ? 1 : 0.35 }}
        />

        <button
          type="button"
          onClick={() => onFocusSpeaker(turn.identity)}
          title={`Show only ${turn.display_name}`}
          className="mt-0.5 shrink-0 rounded-full focus-visible:outline-none"
        >
          <Monogram displayName={turn.display_name} index={colorIndex} size="sm" />
        </button>

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-baseline gap-2.5">
            <span className="text-[0.8125rem] font-semibold text-ink">{turn.display_name}</span>
            {/* Padding grows the hit box to ~52x36 and the equal negative
                margin hands the space straight back, so the header row's
                height — multiplied by a thousand turns — does not change. It is
                the only per-turn control that is not a word, and it is the one
                that always plays.
                Touch only: a negative margin on a FLEX item shrinks its margin
                box and can pull the row's cross size in with it, and this row
                is repeated once per turn. Desktop has a cursor and does not
                need the extra 16px. */}
            <button
              type="button"
              onClick={() => onSeek(turn.start_ms)}
              className="text-xs tabular text-ink-dim transition-colors hover:text-accent max-sm:-m-2 max-sm:p-2"
              title="Play from here"
            >
              {formatDuration(turn.start_ms)}
            </button>
            {turn.confidence !== null && turn.confidence < 0.85 ? (
              <span className="text-[0.6875rem] text-warn" title="Engine confidence for this turn">
                {Math.round(turn.confidence * 100)}% confident
              </span>
            ) : null}
          </div>

          {/* The word hit-area rule, applied ONCE here rather than in every
              one of a thousand word spans.

              Vertical padding on an INLINE element does not affect the line box
              — the text does not reflow and the paragraph does not grow — but
              it does extend the hit area. Measured: the word's box is 19px
              inside a 26px line, so 3.5px above and below it is leading that
              nothing can be tapped in. 5px claims that, and 1.5px more, taking
              the target from 19px to 29px. The 1.5px overlaps the line
              above's own padding in the middle of the gap — which is fine,
              because in that band both words are honest answers to "which one
              did I mean", and either lands the playhead within a second of the
              other. On touch only: with a cursor there is nothing to fix. */}
          <p className="text-[0.9375rem] leading-[1.75] text-ink [@media(pointer:coarse)]:[&>span]:py-[5px]">
            {words
              ? words.map((word, wordIndex) => {
                  const lit = wordIndex === activeWord
                  const matched = needle !== '' && word.word.toLowerCase().includes(needle)
                  const shaky = word.confidence !== null && word.confidence < LOW_CONFIDENCE
                  return (
                    <React.Fragment key={`${wordIndex}-${word.start_ms}`}>
                      {/* Deliberately NOT `role="button"`. A transcript is
                          prose, and giving every word a button role makes a
                          screen reader announce "button" three thousand times
                          instead of reading the conversation. The keyboard path
                          to any point is the turn's timestamp button above,
                          which is one stop per turn rather than one per word. */}
                      <span
                        data-word={wordIndex}
                        onClick={() => onSeekWord(word.start_ms)}
                        title={
                          shaky
                            ? `${formatDuration(word.start_ms)} · ${Math.round((word.confidence ?? 0) * 100)}% confident`
                            : formatDuration(word.start_ms)
                        }
                        className={cn(
                          // No horizontal padding: a pixel either side of every
                          // word compounds across a paragraph and makes prose
                          // read like a word list. The lit state gets its room
                          // from a box-shadow spread instead, which costs no
                          // layout and cannot reflow the text as it plays.
                          'cursor-pointer rounded-[3px] transition-colors',
                          matched && 'bg-warn-soft',
                          shaky && 'underline decoration-dotted underline-offset-[3px]',
                          lit && 'text-ink',
                        )}
                        style={
                          lit
                            ? {
                                // The lit word wears a tint of its own speaker's
                                // colour, so "who is talking" survives even when
                                // the avatar is scrolled off.
                                backgroundColor: `color-mix(in oklab, ${color} 26%, transparent)`,
                                boxShadow: `0 0 0 3px color-mix(in oklab, ${color} 26%, transparent)`,
                              }
                            : undefined
                        }
                      >
                        {word.word}
                      </span>{' '}
                    </React.Fragment>
                  )
                })
              : // A turn with no word timings still has to render. The API is
                // not obliged to provide them, and silently dropping the text
                // would lose transcript content.
                turn.text}
          </p>
        </div>
      </div>
  )
})

function SilenceMarker({ ms }: { ms: number }) {
  return (
    <div className="flex items-center gap-3 px-3 py-1.5 text-[0.6875rem] text-ink-dim">
      <span className="h-px flex-1 bg-line" />
      <span className="tabular">{formatDuration(ms)} of silence</span>
      <span className="h-px flex-1 bg-line" />
    </div>
  )
}

function ToolbarToggle({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean
  onClick: () => void
  /** Carries the meaning below `sm`, where the visible text label is dropped. */
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      title={label}
      className={cn(
        // 44x44 on a phone where it is icon-only, the desktop's 36px pill with
        // its label above `sm`.
        'inline-flex size-11 items-center justify-center gap-1.5 rounded-md border',
        'text-[0.8125rem] font-medium transition-colors',
        'active:bg-surface-3 sm:size-auto sm:h-9 sm:px-3',
        active
          ? 'border-accent-line bg-accent-soft text-accent'
          : 'border-line-strong bg-surface text-ink-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/* the index                                                                   */
/* -------------------------------------------------------------------------- */

interface WordIndex {
  /** Word start times, ascending. The array binary search runs over. */
  starts: number[]
  ends: number[]
  /** `turn << 12 | word` for the entry at the same position. */
  keys: number[]
}

/**
 * Flatten every word in the document into three parallel arrays.
 *
 * Parallel primitive arrays rather than an array of objects: the search runs on
 * every animation frame, and this keeps it to integer comparisons on a
 * contiguous buffer with no property lookups and no allocation.
 *
 * The 12-bit word field caps a turn at 4096 words — about forty minutes of
 * uninterrupted speech from one person without a VAD gap, which the segmenter
 * does not produce. Turns past that are indexed up to the cap rather than
 * mis-keyed.
 */
function buildWordIndex(turns: TranscriptTurn[]): WordIndex {
  const starts: number[] = []
  const ends: number[] = []
  const keys: number[] = []

  turns.forEach((turn, turnIndex) => {
    turn.words.slice(0, 4096).forEach((word, wordIndex) => {
      starts.push(word.start_ms)
      ends.push(word.end_ms)
      keys.push((turnIndex << 12) | wordIndex)
    })
  })

  return { starts, ends, keys }
}

/**
 * The word being spoken at `timeMs`, or -1 in a silence.
 *
 * Returns -1 rather than the nearest word during a gap: highlighting a word
 * nobody is saying, for the several seconds between two turns, makes the
 * highlight look stuck and makes the reader distrust it.
 */
function lookupWord(index: WordIndex, timeMs: number): number {
  const { starts, ends, keys } = index
  if (starts.length === 0) return -1

  let low = 0
  let high = starts.length - 1
  let found = -1
  while (low <= high) {
    const middle = (low + high) >> 1
    if (starts[middle]! <= timeMs) {
      found = middle
      low = middle + 1
    } else {
      high = middle - 1
    }
  }

  if (found < 0) return -1
  // A 120 ms grace so the highlight does not flicker off in the sub-frame gap
  // between one word's end and the next word's start.
  return timeMs <= ends[found]! + 120 ? keys[found]! : -1
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0
  let count = 0
  let at = haystack.indexOf(needle)
  while (at !== -1) {
    count += 1
    at = haystack.indexOf(needle, at + needle.length)
  }
  return count
}
